import axios from '@/plugins/axios'

const getHeaders = () => {
  const userStr = localStorage.getItem('user')
  if (!userStr) return {}
  const { key } = JSON.parse(userStr)
  return { headers: { Authorization: `Token ${key}` } }
}

const VentaCenterStore = {
  namespaced: true,
  state: {
    loading: false,
    error: null,
    warranties: [],
    reservado: false,
    // reservaCorrupta: true → el equipo está bloqueado por OTRO admin (409)
    reservaCorrupta: false,
    // reservaConflicto: datos del conflicto (quién lo tiene reservado)
    reservaConflicto: null,
    contribuyente: null,
    contribuyenteLoading: false,
    contribuyenteError: null,
    // Cupón de descuento
    cuponAplicado: null, // { codigo, porcentaje_descuento }
    cuponLoading: false,
    cuponError: null,
  },
  mutations: {
    SET_LOADING(state, payload) {
      state.loading = payload
    },
    SET_ERROR(state, payload) {
      state.error = payload
    },
    SET_WARRANTIES(state, payload) {
      state.warranties = payload
    },
    SET_RESERVADO(state, payload) {
      state.reservado = payload
    },
    SET_RESERVA_CORRUPTA(state, payload) {
      state.reservaCorrupta = payload
    },
    SET_RESERVA_CONFLICTO(state, payload) {
      state.reservaConflicto = payload
    },
    SET_CONTRIBUYENTE(state, payload) {
      state.contribuyente = payload
    },
    SET_CONTRIBUYENTE_LOADING(state, payload) {
      state.contribuyenteLoading = payload
    },
    SET_CONTRIBUYENTE_ERROR(state, payload) {
      state.contribuyenteError = payload
    },
    SET_CUPON(state, payload) {
      state.cuponAplicado = payload
    },
    SET_CUPON_LOADING(state, payload) {
      state.cuponLoading = payload
    },
    SET_CUPON_ERROR(state, payload) {
      state.cuponError = payload
    },
    RESET_CUPON(state) {
      state.cuponAplicado = null
      state.cuponLoading = false
      state.cuponError = null
    },
  },
  getters: {
    tieneCuponAplicado: (state) => state.cuponAplicado !== null,
    porcentajeCupon: (state) => state.cuponAplicado?.porcentaje_descuento || 0,
    /**
     * montoCupon(subtotal) — getter de orden superior (Vuex 3 / Vue 2).
     * Uso en el componente: this.$store.getters['VentaCenterStore/montoCupon'](subtotal)
     */
    montoCupon: (state) => (subtotal) => {
      if (!state.cuponAplicado) return 0
      return Math.round(
        (subtotal * state.cuponAplicado.porcentaje_descuento) / 100
      )
    },
  },
  actions: {
    async fetchWarranties({ commit }, { precio, equipoId }) {
      try {
        const response = await axios.get('/api/store/garantias-extendidas/', {
          params: { precio, equipo: equipoId },
          ...getHeaders(),
        })
        commit('SET_WARRANTIES', response.data)
      } catch (error) {
        console.error('Error fetching warranties:', error)
      }
    },

    async reservarEquipo({ commit }, payload) {
      // payload: { order_id, equipo_id }
      commit('SET_LOADING', true)
      try {
        await axios.post(
          '/api/store/pedidos/reservar_equipo_center/',
          { equipo_id: payload.equipo_id },
          getHeaders()
        )
        commit('SET_RESERVADO', true)
        commit('SET_RESERVA_CORRUPTA', false)
        commit('SET_RESERVA_CONFLICTO', null)
      } catch (error) {
        if (error.response && error.response.status === 409) {
          // Conflicto: otro admin tiene reserva activa
          commit('SET_RESERVA_CORRUPTA', true)
          commit('SET_RESERVA_CONFLICTO', error.response.data)
          console.warn('Conflicto de reserva:', error.response.data)
        } else if (error.response && error.response.status === 400) {
          // Equipo ya vendido u otro error de validación
          commit('SET_RESERVA_CORRUPTA', true)
          commit('SET_RESERVA_CONFLICTO', null)
          console.error('Error de validación al reservar:', error.response.data)
        } else {
          // Error de red o servidor: no marcamos como corrupta para no bloquear
          // innecesariamente al usuario. El heartbeat reintentará.
          console.error('Error de red al reservar equipo:', error)
        }
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async liberarEquipo({ commit, state }, { equipo_id }) {
      if (!state.reservado) return
      try {
        await axios.post(
          '/api/store/pedidos/liberar_equipo_center/',
          { equipo_id },
          getHeaders()
        )
        commit('SET_RESERVADO', false)
        commit('SET_RESERVA_CORRUPTA', false)
        commit('SET_RESERVA_CONFLICTO', null)
      } catch (error) {
        console.error('Error liberando equipo:', error)
      }
    },

    async fetchContribuyente({ commit }, rut) {
      commit('SET_CONTRIBUYENTE_LOADING', true)
      commit('SET_CONTRIBUYENTE_ERROR', null)
      commit('SET_CONTRIBUYENTE', null)
      try {
        const response = await axios.get(`/api/store/contribuyente/${rut}/`)
        commit('SET_CONTRIBUYENTE', response.data)
      } catch (error) {
        console.error('Error fetching contribuyente:', error)
        if (error.response && [400, 404].includes(error.response.status)) {
          commit(
            'SET_CONTRIBUYENTE_ERROR',
            'Contribuyente no encontrado. Verifique el RUT.'
          )
        } else {
          commit(
            'SET_CONTRIBUYENTE_ERROR',
            'Error al consultar el servicio de facturación.'
          )
        }
      } finally {
        commit('SET_CONTRIBUYENTE_LOADING', false)
      }
    },

    async realizarVenta({ commit }, pedidoData) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const response = await axios.post(
          '/api/store/pedidos/venta_center/',
          pedidoData,
          getHeaders()
        )
        commit('SET_RESERVADO', false) // Se libera al completarse la venta
        return response.data
      } catch (error) {
        const errorMsg = error.response?.data || 'Error al procesar la venta.'
        commit('SET_ERROR', errorMsg)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * Valida un código de cupón contra el endpoint existente de la tienda.
     * Respuesta exitosa: HTTP 200 con { success: true, codigo, porcentaje_descuento, mensaje }
     * Respuesta error:   HTTP 400/404 con { error, disponible: false }
     */
    async validarCupon({ commit }, codigo) {
      commit('SET_CUPON_LOADING', true)
      commit('SET_CUPON_ERROR', null)
      try {
        const response = await axios.post(
          '/api/store/cupones/validar/',
          { codigo: codigo.trim().toUpperCase() },
          getHeaders()
        )
        // HTTP 200 — cupón válido
        const { data } = response
        commit('SET_CUPON', {
          codigo: data.codigo,
          porcentaje_descuento: data.porcentaje_descuento,
          mensaje: data.mensaje,
        })
        commit('SET_CUPON_ERROR', null)
      } catch (error) {
        // HTTP 400 / 404 — cupón inválido, agotado o no existe
        // Siempre se muestra el texto exacto del diseño, independiente del mensaje del backend
        commit('SET_CUPON', null)
        commit(
          'SET_CUPON_ERROR',
          'Lamentablemente el código ingresado no es válido.'
        )
      } finally {
        commit('SET_CUPON_LOADING', false)
      }
    },

    /**
     * Elimina el cupón aplicado y limpia los mensajes.
     */
    resetCupon({ commit }) {
      commit('RESET_CUPON')
    },
  },
}

export default VentaCenterStore
